import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import CheckIcon from '@mui/icons-material/Check';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import SortRoundedIcon from '@mui/icons-material/SortRounded';
import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Box,
  CircularProgress,
  Collapse,
  Dialog,
  DialogContent,
  DialogTitle,
  Menu,
  Stack,
  Tooltip,
  Typography,
  styled,
} from '@mui/material';

import { useQuery } from '@tanstack/react-query';
import { TFunction } from 'i18next';

import { AuditResponse } from '@/api/data-contracts';
import { ReactComponent as ArrowRightIcon } from '@/assets/icons/arrow-right.svg';
import { ROUTES } from '@/config/routes';
import { useMediaPlansService } from '@/features/media-plans';
import { queryKeys } from '@/react-query/constants';
import { DaysLeftTone, daysLeftTone } from '@/shared/lib/daysLeftTone';
import { isVideoFile } from '@/shared/lib/files';
import { formatCurrency, formatNumber } from '@/shared/lib/format';
import { useDeferredOnScreen } from '@/shared/lib/hooks/useOnScreen';
import {
  ImageResource,
  bookingArtworkResource,
  useResourceImage,
} from '@/shared/lib/hooks/useResourceImage';
import {
  ColumnCheckbox,
  ColumnMenuItem,
  menuPaperProps,
} from '@/shared/ui/GeneratikTable/GeneratikTableToolbar.styles';
import { ResourceImage } from '@/shared/ui/ResourceImage';

import useOrdersService from '../api/orders.service';
import {
  ArtworkEntry,
  BOOKING_PERIOD_TYPES,
  DiffDisplayOp,
  HISTORY_PAGE_SIZE,
  HistoryArtworkFocus,
  HistorySubject,
  MediaPlanArtworkEntry,
  PackageBasicInfo,
  SpaceBasicInfo,
  VISUAL_RESOURCE_TYPES,
  abbreviateLastName,
  buildHistorySubjectIndex,
  formatBookingListValue,
  formatDate,
  getArtworkEntries,
  getAvatarColor,
  getBookingPeriod,
  getCountdownInfo,
  getDiffFieldLabel,
  getExtensionDateOp,
  getHistoryArtworkFocus,
  getHistoryBookingId,
  getHistorySubject,
  getInitials,
  getMediaPlanArtworkEntries,
  getOrderActionText,
  getOrderOpsForDisplay,
  getVisualResourceCount,
  groupBulkBookingActionRows,
  historySubjectDetail,
  humanizeServiceList,
  parseResponsibleUsers,
} from './OrderHistoryDialog.utils';

const AvatarBadge = styled(Box)<{ badgecolor: string }>(({ badgecolor }) => ({
  width: 21,
  height: 21,
  borderRadius: '50%',
  backgroundColor: badgecolor,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  fontSize: 9,
  fontWeight: 600,
  flexShrink: 0,
}));

const PillButton = styled(Box)<{ disabled?: boolean }>(({ disabled }) => ({
  display: 'flex',
  alignItems: 'center',
  height: '29px',
  padding: '8px 12px',
  borderRadius: '10px',
  backgroundColor: '#f5f5f7',
  cursor: disabled ? 'default' : 'pointer',
  gap: '5px',
  flexShrink: 0,
  userSelect: 'none',
  opacity: disabled ? 0.4 : 1,
  pointerEvents: disabled ? 'none' : undefined,
  '&:hover': { backgroundColor: disabled ? '#f5f5f7' : '#ebebed' },
}));

const COUNTDOWN_TONE_COLORS: Record<DaysLeftTone, string> = {
  ok: '#2E9E5B',
  warning: '#B77B00',
  critical: '#E9554A',
};

const DiffChip = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 10px',
  borderRadius: '28px',
  height: '16px',
  fontSize: 10,
  fontWeight: 400,
  lineHeight: 1,
  backgroundColor: '#4CAF50',
  color: '#FFFFFF',
  whiteSpace: 'nowrap',
});

const PillButtonText = styled(Typography)({
  fontSize: '0.75rem',
  fontWeight: 400,
  color: '#615E6F',
  lineHeight: '110%',
  whiteSpace: 'nowrap',
});

interface Props {
  open: boolean;
  onClose: () => void;
  orderId?: string;
  mediaPlanId?: string;
  /**
   * The order's / plan's booked spaces and packages, keyed by BOOKING id. Audit rows name their
   * subject by id only, so these are what turn "approved extension" into
   * "… — Billboard · Main entrance · Baneasa Shopping City".
   */
  spaces: SpaceBasicInfo[];
  packages: PackageBasicInfo[];
  canViewSellingPrices?: boolean;
}

function renderDiffValue({ kind }: DiffDisplayOp, val: unknown, t: TFunction) {
  if (kind === 'status') {
    const text = String(val ?? '')
      .replace(/_/g, ' ')
      .toLowerCase();
    return <DiffChip>{text.charAt(0).toUpperCase() + text.slice(1)}</DiffChip>;
  }
  if (kind === 'date') {
    const ts = typeof val === 'number' ? val : parseFloat(String(val));
    const label = isNaN(ts)
      ? String(val)
      : new Date(ts * 1000).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
    return (
      <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>{label}</Typography>
    );
  }
  if (kind === 'iso-date') {
    const parsed = new Date(val as string | number);
    const label = isNaN(parsed.getTime())
      ? String(val)
      : parsed.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return (
      <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>{label}</Typography>
    );
  }
  if (kind === 'number') {
    return (
      <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>
        {formatNumber(Number(val))}
      </Typography>
    );
  }
  if (kind === 'currency') {
    return (
      <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>
        {formatCurrency(Number(val))}
      </Typography>
    );
  }
  if (kind === 'service-list') {
    return (
      <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>
        {humanizeServiceList(String(val ?? ''))}
      </Typography>
    );
  }
  if (kind === 'booking-list') {
    return (
      <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>
        {formatBookingListValue(val)}
      </Typography>
    );
  }
  const str = String(val ?? '');
  const display = str.trim()
    ? str.length > 32
      ? str.slice(0, 29) + '…'
      : str
    : t('order_history.none');
  return (
    <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6, fontStyle: 'italic' }}>
      {display}
    </Typography>
  );
}

const OrderDiffSection: FC<{ op: DiffDisplayOp }> = ({ op }) => {
  const { t } = useTranslation();
  return (
    <Stack direction="row" alignItems="center" gap="6px" flexWrap="wrap">
      <Typography sx={{ fontSize: 10, color: '#333239', fontWeight: 600, lineHeight: 1.6 }}>
        {getDiffFieldLabel(op.op)}:
      </Typography>
      {op.op.oldValue != null ? (
        <>
          {renderDiffValue(op, op.op.oldValue, t)}
          <ArrowRightIcon style={{ width: 12, height: 8, color: '#807D93', flexShrink: 0 }} />
        </>
      ) : null}
      {renderDiffValue(op, op.op.value, t)}
    </Stack>
  );
};

const DiffTableCell = styled(Box)({
  padding: '4px 16px 4px 0',
  fontSize: 10,
  verticalAlign: 'middle',
});

const OrderDiffTable: FC<{ ops: DiffDisplayOp[] }> = ({ ops }) => {
  const { t } = useTranslation();
  return (
    <Box component="table" sx={{ borderCollapse: 'collapse', my: '1rem', ml: '1rem' }}>
      <Box component="tbody">
        {ops.map((entry, i) => (
          <Box component="tr" key={i} data-testid="history-diff-row">
            <DiffTableCell component="td" sx={{ color: '#333239', fontWeight: 600 }}>
              {getDiffFieldLabel(entry.op)}
            </DiffTableCell>
            <DiffTableCell component="td">
              {entry.op.oldValue != null ? renderDiffValue(entry, entry.op.oldValue, t) : null}
            </DiffTableCell>
            <DiffTableCell component="td">
              {entry.op.oldValue != null && (
                <ArrowRightIcon style={{ width: 12, height: 8, color: '#807D93' }} />
              )}
            </DiffTableCell>
            <DiffTableCell component="td">
              {renderDiffValue(entry, entry.op.value, t)}
            </DiffTableCell>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const thumbnailSx = {
  width: 96,
  height: 64,
  borderRadius: '4px',
  flexShrink: 0,
  cursor: 'pointer',
  boxShadow: '0px 1px 3px rgba(0,0,0,0.1), 0px 1px 5px rgba(0,0,0,0.06)',
} as const;

const previewTooltipProps = { tooltip: { sx: { maxWidth: 'none' } } };

const isVideoAttachment = (attachment: ArtworkEntry['attachment']) =>
  isVideoFile(attachment.name) || isVideoFile(attachment.filePointer?.name);

const VideoArtworkThumbnail: FC<{
  resource: ImageResource;
  name: string;
  onClick: () => void;
}> = ({ resource, name, onClick }) => {
  const ref = useRef<HTMLDivElement>(null);
  const isVisible = useDeferredOnScreen(ref);
  const { url: src } = useResourceImage(resource, { size: { tier: 'full' }, enabled: isVisible });

  return (
    <Tooltip
      title={
        src ? (
          <Box
            component="video"
            src={src}
            autoPlay
            muted
            loop
            playsInline
            sx={{ width: 360, height: 'auto', display: 'block' }}
          />
        ) : (
          name
        )
      }
      placement="top"
      arrow
      componentsProps={previewTooltipProps}
    >
      <Box
        ref={ref}
        role="button"
        aria-label={name}
        data-testid="history-artwork-video-thumbnail"
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        sx={{
          ...thumbnailSx,
          position: 'relative',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: (theme) => theme.palette.grey[100],
        }}
      >
        {src ? (
          <>
            <Box
              component="video"
              src={`${src}#t=0.1`}
              muted
              playsInline
              preload="metadata"
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
            <PlayArrowRoundedIcon
              sx={{
                position: 'absolute',
                fontSize: '1.5rem',
                color: '#fff',
                backgroundColor: 'rgba(0,0,0,0.45)',
                borderRadius: '50%',
                pointerEvents: 'none',
              }}
            />
          </>
        ) : (
          <VideocamIcon sx={{ fontSize: '2rem', color: '#6B7280' }} />
        )}
      </Box>
    </Tooltip>
  );
};

const ArtworkThumbnail: FC<{
  entry: ArtworkEntry;
  isPreview: boolean;
  onGoToBooking?: (bookingId: string, artworkFocus?: HistoryArtworkFocus | null) => void;
}> = ({ entry, isPreview, onGoToBooking }) => {
  const resource = useMemo<ImageResource>(
    () =>
      bookingArtworkResource(
        { orderId: entry.orderId, bookingId: entry.bookingId },
        entry.objectId,
        { preview: isPreview },
      ),
    [entry.orderId, entry.bookingId, entry.objectId, isPreview],
  );

  const isVideo = isVideoAttachment(entry.attachment);
  const { url: src } = useResourceImage(resource, { size: { tier: 'thumb' }, enabled: !isVideo });

  if (isVideo) {
    return (
      <VideoArtworkThumbnail
        resource={resource}
        name={entry.attachment.name}
        onClick={() =>
          onGoToBooking?.(entry.bookingId, { attachmentId: entry.attachment.id, isPreview })
        }
      />
    );
  }

  if (!src) return null;

  return (
    <Tooltip
      title={
        <ResourceImage
          resource={resource}
          size={{ tier: 'preview' }}
          alt="artwork"
          placeholder="none"
          fadeIn={false}
          sx={{ width: 360, height: 'auto', display: 'block' }}
        />
      }
      placement="top"
      arrow
      componentsProps={previewTooltipProps}
    >
      <Box
        component="img"
        src={src}
        onClick={(e) => {
          e.stopPropagation();
          onGoToBooking?.(entry.bookingId, { attachmentId: entry.attachment.id, isPreview });
        }}
        sx={{ ...thumbnailSx, objectFit: 'cover' }}
      />
    </Tooltip>
  );
};

const MediaPlanArtworkThumbnail: FC<{
  entry: MediaPlanArtworkEntry;
  onGoToBooking?: (bookingId: string, artworkFocus?: HistoryArtworkFocus | null) => void;
}> = ({ entry, onGoToBooking }) => {
  const kind = entry.isPreview ? 'mediaplan-artwork-preview' : 'mediaplan-artwork';

  const resource = useMemo<ImageResource>(
    () =>
      ({
        kind,
        planId: entry.planId,
        bookingId: entry.bookingId,
        documentId: entry.objectId,
      }) as ImageResource,
    [kind, entry.planId, entry.bookingId, entry.objectId],
  );

  const artworkFocus: HistoryArtworkFocus = {
    attachmentId: entry.attachment.id,
    isPreview: entry.isPreview,
  };

  const isVideo = isVideoAttachment(entry.attachment);
  const { url: src } = useResourceImage(resource, { size: { tier: 'thumb' }, enabled: !isVideo });

  if (isVideo) {
    return (
      <VideoArtworkThumbnail
        resource={resource}
        name={entry.attachment.name}
        onClick={() => onGoToBooking?.(entry.bookingId, artworkFocus)}
      />
    );
  }

  if (!src) return null;

  return (
    <Tooltip
      title={
        <ResourceImage
          resource={resource}
          size={{ tier: 'preview' }}
          alt="artwork"
          placeholder="none"
          fadeIn={false}
          sx={{ width: 360, height: 'auto', display: 'block' }}
        />
      }
      placement="top"
      arrow
      componentsProps={previewTooltipProps}
    >
      <Box
        component="img"
        src={src}
        onClick={(e) => {
          e.stopPropagation();
          onGoToBooking?.(entry.bookingId, artworkFocus);
        }}
        sx={{ ...thumbnailSx, objectFit: 'cover' }}
      />
    </Tooltip>
  );
};

const ResponsibleBadges: FC<{ details?: string | null }> = ({ details }) => {
  const users = useMemo(() => parseResponsibleUsers(details), [details]);
  if (!users.length) return null;
  return (
    <Stack direction="row" alignItems="center" gap="6px" flexWrap="wrap">
      {users.map((u) => (
        <Stack key={u.userId || u.userName} direction="row" alignItems="center" gap="4px">
          <AvatarBadge
            badgecolor={getAvatarColor(u.userId || u.userName)}
            sx={{ width: 18, height: 18, fontSize: 8, marginTop: 0 }}
          >
            {getInitials(u.userName)}
          </AvatarBadge>
          <Typography sx={{ fontSize: 10, color: '#333239', lineHeight: 1.6 }}>
            {abbreviateLastName(u.userName)}
          </Typography>
        </Stack>
      ))}
    </Stack>
  );
};

const HistoryItem: FC<{
  item: AuditResponse;
  actionText: string;
  subject: HistorySubject | null;
  onGoToBooking?: (bookingId: string, artworkFocus?: HistoryArtworkFocus | null) => void;
  canViewSellingPrices?: boolean;
}> = ({ item, actionText, subject, onGoToBooking, canViewSellingPrices }) => {
  const [diffExpanded, setDiffExpanded] = useState(false);
  const diffOps = useMemo(
    () => (item.extraDetails ? getOrderOpsForDisplay(item.extraDetails, canViewSellingPrices) : []),
    [item.extraDetails, canViewSellingPrices],
  );
  const bookingId = useMemo(() => getHistoryBookingId(item), [item]);
  const artworkFocus = useMemo(() => getHistoryArtworkFocus(item), [item]);
  const hasDiff = diffOps.length > 0;
  const showResponsible = item.type === 'ORDER_LOCATION_RESPONSIBLE_ASSIGNED';
  const showArtwork = item.type === 'ORDER_BOOKING_ARTWORK_UPDATED';
  const showBookingPeriod = BOOKING_PERIOD_TYPES.has(item.type);
  const showVisualCount = VISUAL_RESOURCE_TYPES.has(item.type);
  const visualCount = useMemo(
    () => (showVisualCount ? getVisualResourceCount(item.details) : null),
    [showVisualCount, item.details],
  );
  const showMediaPlanArtwork = item.type === 'MEDIA_PLAN_BOOKING_ARTWORK_UPDATED';
  const mediaPlanArtworkEntries = useMemo(
    () => (showMediaPlanArtwork ? getMediaPlanArtworkEntries(item) : []),
    [showMediaPlanArtwork, item],
  );

  const artworkEntries = useMemo(
    () => (showArtwork ? getArtworkEntries(item) : []),
    [showArtwork, item],
  );
  const bookingPeriod = useMemo(
    () => (showBookingPeriod ? getBookingPeriod(item.details) : null),
    [showBookingPeriod, item.details],
  );
  const subjectDetail = subject ? historySubjectDetail(subject) : '';
  const countdown = useMemo(() => getCountdownInfo(item.details), [item.details]);
  const showCountdown = !!countdown;
  const extensionDateOp = useMemo(() => getExtensionDateOp(item.extraDetails), [item.extraDetails]);
  const showExtensionDate = !!extensionDateOp;

  const showDiff =
    hasDiff &&
    !showResponsible &&
    !showArtwork &&
    !showMediaPlanArtwork &&
    !showBookingPeriod &&
    !showVisualCount &&
    !showCountdown;

  const hasInlineDetail =
    showResponsible ||
    (showArtwork && artworkEntries.length > 0) ||
    (showMediaPlanArtwork && mediaPlanArtworkEntries.length > 0) ||
    showBookingPeriod ||
    (showVisualCount && !!visualCount) ||
    showCountdown ||
    showExtensionDate ||
    showDiff;

  const isExpandable = showDiff && diffOps.length > 1;

  return (
    <Stack
      direction="row"
      alignItems="flex-start"
      gap="8px"
      flexWrap="wrap"
      sx={{ py: '8px', px: 0, width: '100%', cursor: isExpandable ? 'pointer' : 'default' }}
      onClick={isExpandable ? () => setDiffExpanded((prev) => !prev) : undefined}
      data-testid="history-item"
    >
      <AvatarBadge badgecolor={getAvatarColor(item.userId ?? item.userName)}>
        {getInitials(item.userName)}
      </AvatarBadge>
      <Stack sx={{ flexGrow: 1 }}>
        <Stack direction="row" alignItems="flex-start" gap="8px" flexWrap="wrap">
          <Typography
            data-testid={onGoToBooking && bookingId ? 'history-go-to-booking' : undefined}
            onClick={
              onGoToBooking && bookingId
                ? (e) => {
                    e.stopPropagation();
                    onGoToBooking(bookingId, artworkFocus);
                  }
                : undefined
            }
            sx={{
              fontSize: '0.825rem',
              color: '#333239',
              lineHeight: 1.6,
              cursor: onGoToBooking && bookingId ? 'pointer' : 'default',
              '&:hover': onGoToBooking && bookingId ? { textDecoration: 'underline' } : undefined,
            }}
          >
            <Box component="span" fontWeight={600}>
              {abbreviateLastName(item.userName)}
            </Box>{' '}
            {actionText}
          </Typography>
          {isExpandable && (
            <KeyboardArrowDownRoundedIcon
              data-testid="history-diff-toggle"
              sx={{
                fontSize: 18,
                color: '#807D93',
                flexShrink: 0,
                transform: diffExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.15s ease',
              }}
            />
          )}
        </Stack>
        {subject && (
          <Typography
            data-testid="history-subject"
            sx={{ fontSize: '0.75rem', lineHeight: 1.5, color: '#504E5A' }}
          >
            {subject.name && (
              <Box component="span" sx={{ color: '#333239', fontWeight: 500 }}>
                {subject.name}
              </Box>
            )}
            {subjectDetail && `${subject.name ? ' \u00b7 ' : ''}${subjectDetail}`}
          </Typography>
        )}
        {hasInlineDetail && (
          <Stack direction="row" alignItems="center" gap="8px" flexWrap="wrap" sx={{ mt: '6px' }}>
            {showResponsible && <ResponsibleBadges details={item.details} />}
            {showArtwork &&
              artworkEntries.map((entry) => (
                <ArtworkThumbnail
                  key={entry.objectId}
                  entry={entry}
                  isPreview={artworkFocus?.isPreview ?? false}
                  onGoToBooking={onGoToBooking}
                />
              ))}
            {showMediaPlanArtwork &&
              mediaPlanArtworkEntries.map((entry) => (
                <MediaPlanArtworkThumbnail
                  key={entry.objectId}
                  entry={entry}
                  onGoToBooking={onGoToBooking}
                />
              ))}
            {showBookingPeriod && bookingPeriod && (
              <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>
                {bookingPeriod}
              </Typography>
            )}
            {showVisualCount && visualCount && (
              <Stack direction="row" alignItems="center" gap="6px">
                <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>
                  {visualCount.prev}
                </Typography>
                <ArrowRightIcon style={{ width: 12, height: 8, color: '#807D93', flexShrink: 0 }} />
                <Typography sx={{ fontSize: 10, color: '#504E5A', lineHeight: 1.6 }}>
                  {visualCount.next}
                </Typography>
              </Stack>
            )}
            {showCountdown && countdown && (
              <Typography
                data-testid="history-countdown-text"
                sx={{
                  fontSize: 10,
                  fontWeight: 600,
                  color: COUNTDOWN_TONE_COLORS[daysLeftTone(countdown.days)],
                  lineHeight: 1.6,
                }}
              >
                {countdown.days} {countdown.isOverdue ? 'days overdue' : 'days left'}
                {countdown.date ? ` \u00b7 starts ${countdown.date}` : ''}
              </Typography>
            )}
            {showExtensionDate && extensionDateOp && <OrderDiffSection op={extensionDateOp} />}
            {showDiff && diffOps.length === 1 && <OrderDiffSection op={diffOps[0]} />}
          </Stack>
        )}
        <Typography sx={{ fontSize: 10, color: '#807D93' }}>
          {formatDate(item.timestamp)}
        </Typography>
        {isExpandable && (
          <Collapse in={diffExpanded} unmountOnExit>
            <OrderDiffTable ops={diffOps} />
          </Collapse>
        )}
      </Stack>
    </Stack>
  );
};

const OrderHistoryDialog: FC<Props> = ({
  open,
  onClose,
  orderId,
  mediaPlanId,
  spaces,
  packages,
  canViewSellingPrices,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getOrderHistory } = useOrdersService();
  const { getMediaPlanHistory } = useMediaPlansService();

  const handleGoToBooking = orderId
    ? (bookingId: string, artworkFocus?: HistoryArtworkFocus | null) => {
        const params = new URLSearchParams({
          highlightBookingId: bookingId,
          notificationFocus: '1',
        });
        if (artworkFocus) {
          params.set(artworkFocus.isPreview ? 'openArtworkPreview' : 'openArtwork', '1');
          params.set('focusAttachmentId', artworkFocus.attachmentId);
        }
        navigate(`${ROUTES.AUTHORIZED.ORDERS}/${orderId}?${params.toString()}`);
        onClose();
      }
    : mediaPlanId
      ? (bookingId: string, artworkFocus?: HistoryArtworkFocus | null) => {
          const params = new URLSearchParams({
            highlightBookingId: bookingId,
            notificationFocus: '1',
          });
          if (artworkFocus) {
            params.set(artworkFocus.isPreview ? 'openArtworkPreview' : 'openArtwork', '1');
            params.set('focusAttachmentId', artworkFocus.attachmentId);
          }
          navigate(`${ROUTES.AUTHORIZED.MEDIA_PLANS}/${mediaPlanId}?${params.toString()}`);
          onClose();
        }
      : undefined;

  const [visibleCount, setVisibleCount] = useState(HISTORY_PAGE_SIZE);
  const [sortDesc, setSortDesc] = useState(true);
  const [filterAnchor, setFilterAnchor] = useState<HTMLElement | null>(null);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [sentinelEl, setSentinelEl] = useState<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  const { data: allOrderItems = [], isFetching: isOrderFetching } = useQuery({
    queryKey: [queryKeys.orderHistory, orderId],
    queryFn: () => getOrderHistory(orderId!),
    enabled: open && !!orderId,
    staleTime: 0,
    cacheTime: 0,
  });

  const { data: allMediaPlanItems = [], isFetching: isMediaPlanFetching } = useQuery({
    queryKey: [queryKeys.orderHistory, mediaPlanId],
    queryFn: () => getMediaPlanHistory(mediaPlanId!),
    enabled: open && !!mediaPlanId,
    staleTime: 0,
    cacheTime: 0,
  });

  const isLoading = isOrderFetching || isMediaPlanFetching;

  const subjectIndex = useMemo(
    () => buildHistorySubjectIndex(spaces, packages),
    [spaces, packages],
  );

  const getActionText = (item: AuditResponse) => {
    const computed = getOrderActionText(item.type, item.extraDetails, item.details);
    if (computed) return computed;
    return t(`order_history.events.${item.type}`, {
      defaultValue: item.type.toLowerCase().replace(/_/g, ' '),
    });
  };

  const allItems = orderId ? allOrderItems : allMediaPlanItems;

  const filteredItems = useMemo(() => {
    const base =
      selectedTypes.length > 0 ? allItems.filter((i) => selectedTypes.includes(i.type)) : allItems;
    const sorted = sortDesc ? base : [...base].reverse();
    return groupBulkBookingActionRows(sorted);
  }, [allItems, selectedTypes, sortDesc]);

  useEffect(() => {
    setVisibleCount(HISTORY_PAGE_SIZE);
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [selectedTypes, sortDesc, orderId, mediaPlanId]);

  const hasMore = visibleCount < filteredItems.length;

  const displayedItems = useMemo(
    () => filteredItems.slice(0, visibleCount),
    [filteredItems, visibleCount],
  );

  const availableTypes = useMemo(
    () => [...new Set(allItems.map((i) => i.type))].sort(),
    [allItems],
  );

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type],
    );
  };

  const handleSort = () => setSortDesc((p) => !p);

  const hasMoreRef = useRef(hasMore);
  hasMoreRef.current = hasMore;

  const totalRef = useRef(filteredItems.length);
  totalRef.current = filteredItems.length;

  useEffect(() => {
    if (!sentinelEl) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMoreRef.current) {
          setVisibleCount((prev) => Math.min(prev + HISTORY_PAGE_SIZE, totalRef.current));
        }
      },
      // threshold 0, not 0.1: the target is a 1px sentinel, and a 10% visibility floor on a
      // 1px box is a sub-pixel gamble at the scroller's edge. Any intersection at all means
      // the user reached the end of the loaded page.
      { root: scrollRef.current, threshold: 0 },
    );

    observer.observe(sentinelEl);
    return () => observer.disconnect();
  }, [sentinelEl]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      data-testid="order-history-dialog"
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          background: '#FEFEFE',
          padding: 0,
          paddingBottom: '1rem',
        }}
      >
        <Typography
          sx={{ flex: 1, color: '#504E5A', fontSize: '16px', fontWeight: 500, lineHeight: '130%' }}
        >
          {t('order_history.title')}
        </Typography>

        <Stack direction="row" gap="8px">
          <PillButton onClick={handleSort} data-testid="sort-toggle-button">
            <SortRoundedIcon sx={{ fontSize: 16, color: '#807d93' }} />
            <PillButtonText>{sortDesc ? 'Newest' : 'Oldest'}</PillButtonText>
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20, color: '#807d93' }} />
          </PillButton>

          <PillButton
            onClick={(e) =>
              availableTypes.length > 0 && setFilterAnchor(e.currentTarget as HTMLElement)
            }
            disabled={availableTypes.length === 0}
            data-testid="filter-button"
          >
            <FilterAltRoundedIcon sx={{ fontSize: 16, color: '#807d93' }} />
            <PillButtonText>Filter</PillButtonText>
            <KeyboardArrowDownRoundedIcon sx={{ fontSize: 20, color: '#807d93' }} />
          </PillButton>

          <PillButton
            onClick={onClose}
            sx={{ width: 44, height: 30, borderRadius: '12px', justifyContent: 'center', gap: 0 }}
            data-testid="close-history-button"
          >
            <CloseRoundedIcon sx={{ fontSize: 16, color: '#807d93' }} />
          </PillButton>
        </Stack>
      </DialogTitle>

      <Menu
        anchorEl={filterAnchor}
        open={!!filterAnchor}
        onClose={() => setFilterAnchor(null)}
        slotProps={{ paper: { sx: { ...menuPaperProps.sx, maxHeight: 300 } } }}
      >
        {availableTypes.map((type) => (
          <ColumnMenuItem key={type} onClick={() => toggleType(type)}>
            <Stack direction="row" alignItems="center" gap={1}>
              <ColumnCheckbox checked={selectedTypes.includes(type)}>
                <CheckIcon />
              </ColumnCheckbox>
              {t(`order_history.events.${type}`, {
                defaultValue: type.replace(/_/g, ' '),
              }).replace(/^./, (c) => c.toUpperCase())}
            </Stack>
          </ColumnMenuItem>
        ))}
      </Menu>

      <DialogContent ref={scrollRef} sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
        {isLoading && displayedItems.length === 0 ? (
          <Box display="flex" justifyContent="center" py={4} flex={1}>
            <CircularProgress size={28} />
          </Box>
        ) : displayedItems.length === 0 ? (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            gap="8px"
            flex={1}
            pb={4}
            data-testid="history-empty-state"
          >
            <AccessTimeRoundedIcon sx={{ fontSize: 36, color: '#807d93' }} />
            <Typography
              sx={{
                fontSize: 12,
                color: '#504E5A',
                lineHeight: 1.6,
                textAlign: 'center',
                maxWidth: 336,
              }}
            >
              {t('order_history.no_activity')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
              alignSelf: 'stretch',
            }}
            data-testid="history-items-list"
          >
            {displayedItems.map((item) => (
              <HistoryItem
                key={item.id}
                item={item}
                actionText={getActionText(item)}
                subject={getHistorySubject(item, subjectIndex)}
                onGoToBooking={handleGoToBooking}
                canViewSellingPrices={canViewSellingPrices}
              />
            ))}
          </Box>
        )}

        {hasMore && (
          <Stack alignItems="center" sx={{ py: '1rem' }}>
            <CircularProgress size={20} sx={{ color: '#807D93' }} />
          </Stack>
        )}

        {/* flexShrink: 0 is load-bearing. DialogContent is a column flex container whose
            content overflows, so every flex item shrinks — and an empty 1px box has
            min-height: auto = 0, so it collapsed to 0px and sat exactly on the scroller's
            bottom edge. A zero-area target never reaches an intersection threshold, so the
            observer never fired and nobody could page past the first HISTORY_PAGE_SIZE rows
            (found 2026-09-03 by the order-history i18n E2E, which stubs 75 rows). */}
        <Box ref={setSentinelEl} sx={{ height: '1px', flexShrink: 0 }} />
      </DialogContent>
    </Dialog>
  );
};

export default OrderHistoryDialog;
